import React, { useState, useEffect } from 'react';
import Textarea from '../../../components/ui/Textarea';
import { useToast } from '../../../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { billingService } from '../../../services/billingService';
import { formatDateTime } from '../../../utils/formatters';

const money = (n) => `${Number(n || 0).toLocaleString()} DZD`;

/**
 * Master-admin billing management: review payment receipts (approve/reject),
 * edit plan pricing, and set the payment instructions (CCP/RIP/Baridi Mob).
 */
const BillingAdmin = () => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('billing');
  const [tab, setTab] = useState('payments');
  const [payments, setPayments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [receiptUrls, setReceiptUrls] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [pay, pl, st] = await Promise.all([
        billingService.getAllPayments(),
        billingService.getPlans({ includeInactive: true }),
        billingService.getPaymentSettings()
      ]);
      setPayments(pay);
      setPlans(pl);
      setSettings(st);
    } catch (e) {
      setFeedback({ type: 'error', text: e?.message || t('admin.feedback.loadFailed') });
    } finally {
      setLoading(false);
    }
  };

  const viewReceipt = async (payment) => {
    if (receiptUrls[payment?.id]) { window.open(receiptUrls[payment?.id], '_blank'); return; }
    const url = await billingService.getReceiptUrl(payment?.receiptUrl);
    if (url) { setReceiptUrls(prev => ({ ...prev, [payment?.id]: url })); window.open(url, '_blank'); }
  };

  const handleApprove = async (payment) => {
    setBusyId(payment?.id);
    try {
      await billingService.approvePayment(payment?.id);
      await load();
    } catch (e) { toastError(e?.message || t('admin.feedback.approveFailed')); }
    finally { setBusyId(null); }
  };

  const handleReject = async (payment) => {
    const note = window.prompt(t('admin.rejectPrompt'), '');
    if (note === null) return;
    setBusyId(payment?.id);
    try {
      await billingService.rejectPayment(payment?.id, note || null);
      await load();
    } catch (e) { toastError(e?.message || t('admin.feedback.rejectFailed')); }
    finally { setBusyId(null); }
  };

  const savePlan = async (plan) => {
    try {
      await billingService.updatePlan(plan?.id, plan);
      setFeedback({ type: 'success', text: t('admin.feedback.planSaved') });
      await load();
    } catch (e) { setFeedback({ type: 'error', text: e?.message || t('admin.feedback.planSaveFailed') }); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await billingService.updatePaymentSettings(settings);
      setFeedback({ type: 'success', text: t('admin.feedback.settingsSaved') });
    } catch (e) { setFeedback({ type: 'error', text: e?.message || t('admin.feedback.settingsSaveFailed') }); }
    finally { setSavingSettings(false); }
  };

  const statusBadge = (status) => ({
    pending: 'bg-warning/10 text-warning',
    approved: 'bg-success/10 text-success',
    rejected: 'bg-error/10 text-error'
  }[status] || 'bg-muted text-muted-foreground');

  const pending = payments?.filter(p => p?.status === 'pending');

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Icon name="Loader2" size={32} className="text-primary animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-border">
        {[
          { id: 'payments', label: `${t('admin.tabs.payments')}${pending?.length ? ` (${pending.length})` : ''}` },
          { id: 'plans', label: t('admin.tabs.plans') },
          { id: 'settings', label: t('admin.tabs.settings') }
        ]?.map(x => (
          <button
            key={x?.id}
            onClick={() => setTab(x?.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === x?.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {x?.label}
          </button>
        ))}
      </div>

      {feedback && (
        <div className={`p-3 rounded-lg text-sm ${feedback?.type === 'error' ? 'bg-error/10 text-error' : 'bg-success/10 text-success'}`}>{feedback?.text}</div>
      )}

      {/* Payments queue */}
      {tab === 'payments' && (
        payments?.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">{t('admin.noPayments')}</p>
        ) : (
          <div className="space-y-2">
            {payments?.map((p) => (
              <div key={p?.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    {p?.user ? `${p?.user?.firstName} ${p?.user?.lastName}` : t('admin.unknownUser')} · {p?.planName || t('planFallback')} · {money(p?.amountDzd)}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {p?.user?.email} · {formatDateTime(p?.createdAt)} · {p?.referenceCode}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {p?.receiptUrl && (
                    <Button variant="ghost" size="sm" onClick={() => viewReceipt(p)} iconName="FileText" iconPosition="left">{t('admin.receipt')}</Button>
                  )}
                  {p?.status === 'pending' ? (
                    <>
                      <Button variant="outline" size="sm" onClick={() => handleReject(p)} loading={busyId === p?.id}>{t('admin.reject')}</Button>
                      <Button size="sm" onClick={() => handleApprove(p)} loading={busyId === p?.id} iconName="Check" iconPosition="left">{t('admin.approve')}</Button>
                    </>
                  ) : (
                    <span className={`px-2 py-0.5 text-[11px] font-medium ${statusBadge(p?.status)}`}>{t(`status.${p?.status}`, p?.status)}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Plans editor */}
      {tab === 'plans' && (
        <div className="space-y-3">
          {plans?.map((plan, i) => (
            <div key={plan?.id} className="rounded-lg border border-border bg-card p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Input label={t('admin.fields.name')} value={plan?.name || ''} onChange={(e) => setPlans(prev => prev.map((x, j) => j === i ? { ...x, name: e?.target?.value } : x))} />
                <Input label={t('admin.fields.price')} type="number" value={plan?.priceDzd ?? 0} onChange={(e) => setPlans(prev => prev.map((x, j) => j === i ? { ...x, priceDzd: Number(e?.target?.value) } : x))} />
                {plan?.planType === 'annual' ? (
                  <Input label={t('admin.fields.duration')} type="number" value={plan?.durationDays ?? 365} onChange={(e) => setPlans(prev => prev.map((x, j) => j === i ? { ...x, durationDays: Number(e?.target?.value) } : x))} />
                ) : (
                  <Input label={t('admin.fields.credits')} type="number" value={plan?.credits ?? 0} onChange={(e) => setPlans(prev => prev.map((x, j) => j === i ? { ...x, credits: Number(e?.target?.value) } : x))} />
                )}
                <div className="flex items-end gap-2">
                  <label className="flex items-center gap-1.5 text-sm text-foreground">
                    <input type="checkbox" checked={!!plan?.isActive} onChange={(e) => setPlans(prev => prev.map((x, j) => j === i ? { ...x, isActive: e?.target?.checked } : x))} className="w-4 h-4" />
                    {t('admin.fields.active')}
                  </label>
                  <Button size="sm" onClick={() => savePlan(plans[i])} iconName="Save" iconPosition="left">{t('admin.fields.save')}</Button>
                </div>
              </div>
              <Input className="mt-3" label={t('admin.fields.description')} value={plan?.description || ''} onChange={(e) => setPlans(prev => prev.map((x, j) => j === i ? { ...x, description: e?.target?.value } : x))} />
            </div>
          ))}
        </div>
      )}

      {/* Payment settings */}
      {tab === 'settings' && settings && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-3 max-w-xl">
          <p className="text-xs text-muted-foreground">{t('admin.settings.intro')}</p>
          <Input label={t('admin.settings.baridimob')} value={settings?.baridimobNumber || ''} onChange={(e) => setSettings(prev => ({ ...prev, baridimobNumber: e?.target?.value }))} />
          <Input label={t('admin.settings.ccp')} value={settings?.ccpNumber || ''} onChange={(e) => setSettings(prev => ({ ...prev, ccpNumber: e?.target?.value }))} />
          <Input label={t('admin.settings.rip')} value={settings?.ripNumber || ''} onChange={(e) => setSettings(prev => ({ ...prev, ripNumber: e?.target?.value }))} />
          <div>
            <Textarea
              label={t('admin.settings.instructions')}
              value={settings?.instructions || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, instructions: e?.target?.value }))}
              rows={5}
              placeholder={t('admin.settings.instructionsPlaceholder')}
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div className="flex justify-end">
            <Button onClick={saveSettings} loading={savingSettings} iconName="Save" iconPosition="left">{t('admin.settings.save')}</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingAdmin;
